import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  ArrowLeft, Save, CheckCircle2, XCircle, Eye, EyeOff, ChevronUp, ChevronDown,
  Trash2, Plus, ExternalLink, Sparkles, Send, Mail,
} from 'lucide-react';
import api from '@/utils/api';
import TzAwareDatetimeInput from '@/components/ui/TzAwareDatetimeInput';
import VisualBlockEditor from './components/VisualBlockEditor';
import GoldenTicketsPanel from './components/GoldenTicketsPanel';
import HeroImageUploader from './components/HeroImageUploader';
import PaymentsPanel from './components/PaymentsPanel';

const EVENT_TYPES = [
  'local_gathering', 'dinner', 'lounge_event', 'community_meetup',
  'workshop', 'activity', 'retreat', 'domestic_travel',
  'international_travel', 'private', 'other',
];

const BLOCK_TYPES = [
  'hero', 'overview', 'date_time', 'venue', 'schedule', 'what_to_expect',
  'food', 'gallery', 'dress_code', 'faq', 'policies', 'hosts',
  'sponsors', 'custom_rich_text', 'rsvp_cta',
];

// Bundle 136 P0-5 - Pixel status pill. Confirms Meta pixel loaded
// globally (not per-event). Green means new events auto-inherit
// the pixel and admins do not need to configure anything per event.
const PixelStatusPill = () => {
  const [state, setState] = React.useState('loading');
  React.useEffect(() => {
    const t = setTimeout(() => {
      try {
        const loaded = typeof window !== 'undefined'
          && typeof window.fbq === 'function';
        setState(loaded ? 'auto' : 'missing');
      } catch (_e) { setState('missing'); }
    }, 800);
    return () => clearTimeout(t);
  }, []);
  if (state === 'loading') {
    return (
      <span data-testid="pixel-status-pill-loading"
              className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[11px] text-neutral-500">
        Pixel: checking...
      </span>
    );
  }
  if (state === 'auto') {
    return (
      <span data-testid="pixel-status-pill-auto"
              title="Meta pixel loaded globally. New events auto-inherit."
              className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-800">
        Pixel: auto-configured
      </span>
    );
  }
  return (
    <span data-testid="pixel-status-pill-missing"
            title="Meta pixel did not load. Check /api/pixels/active."
            className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800">
      Pixel: missing
    </span>
  );
};

const AdminEventEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/events/${id}`);
      setPayload(data);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  const event = payload?.event;

  const patch = async (body) => {
    setSaving(true);
    try {
      await api.patch(`/admin/events/${id}`, body);
      await reload();
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1500);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const publish = async () => {
    try {
      await api.post(`/admin/events/${id}/publish`);
      await reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const missing = d?.missing_fields || [];
      if (missing.length) {
        alert('Cannot publish yet - missing: ' + missing.map((m) => m.label).join(', '));
      } else {
        alert(typeof d === 'string' ? d : 'Publish failed');
      }
    }
  };

  const unpublish = async () => {
    await api.post(`/admin/events/${id}/unpublish`);
    await reload();
  };

  if (loading || !event) {
    return <div className="p-16 flex justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-700" /></div>;
  }

  const missing = payload?.missing_required_fields || [];
  const publishReady = payload?.publish_ready;

  return (
    <div className="p-8 max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin/events" className="text-sm text-neutral-500 hover:text-primary-800 inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3.5 w-3.5" /> All events
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl font-bold text-primary-900">{event.title}</h1>
            <Badge variant="outline" className="capitalize">{(event.state || 'draft').replace(/_/g, ' ')}</Badge>
            <PixelStatusPill />
            {savedTick && <span className="text-xs text-success-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Saved</span>}
          </div>
          <div className="text-xs text-neutral-500 mt-1 font-mono">/{event.slug}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline">
            <Link to={`/admin/events/${id}/registrations`}>Registrations</Link>
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              const to = window.prompt('Send test ticket email to:', 'yaar@dostination.com');
              if (!to) return;
              try {
                const { data } = await api.post(`/admin/events/${id}/test-ticket-email`, { to });
                alert(`Test ticket email sent to ${data.sent_to}.`);
              } catch (err) {
                alert(err?.response?.data?.detail || 'Test email failed');
              }
            }}
            title="Send a fully-rendered ticket email with a synthetic reg"
            data-testid="event-edit-test-ticket-btn"
          >
            <Mail className="h-4 w-4 mr-2" /> Send test ticket email
          </Button>
          {event.state === 'published' ? (
            <>
              <Button asChild variant="outline">
                <a href={`/events/${event.slug}`} target="_blank" rel="noopener noreferrer">
                  Preview <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              </Button>
              <Button variant="outline" onClick={unpublish}>Unpublish</Button>
            </>
          ) : (
            <Button
              onClick={publish}
              disabled={!publishReady}
              title={publishReady ? '' : 'Complete required fields first'}
              className="bg-gradient-to-r from-secondary-500 to-secondary-600 text-primary-900 font-semibold shadow-gold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-4 w-4 mr-2" /> Publish
            </Button>
          )}
        </div>
      </div>

      {/* Required-to-publish checklist */}
      {!publishReady && (
        <Card className="border shadow-soft bg-yellow-50/40 border-yellow-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-yellow-700" />
              <span className="font-semibold text-yellow-900">Required to publish ({missing.length} remaining)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {missing.map((m) => (
                <span key={m.field} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-yellow-200 text-xs text-yellow-800 font-medium">
                  <XCircle className="h-3 w-3" /> {m.label}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="basics" className="w-full">
        <TabsList className="grid grid-cols-3 md:grid-cols-10 w-full">
          <TabsTrigger value="basics">Basics</TabsTrigger>
          <TabsTrigger value="blocks">Content</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="golden_tickets" data-testid="tab-golden-tickets">Golden Tickets</TabsTrigger>
          <TabsTrigger value="reg_fields">Reg. Fields</TabsTrigger>
          <TabsTrigger value="capacity">Capacity</TabsTrigger>
          <TabsTrigger value="seo">SEO &amp; Share</TabsTrigger>
          <TabsTrigger value="publish">Publish</TabsTrigger>
          <TabsTrigger value="survey" data-testid="tab-survey">Survey</TabsTrigger>
        </TabsList>

        <TabsContent value="basics" className="pt-6">
          <BasicsPanel event={event} onSave={patch} saving={saving} />
        </TabsContent>
        <TabsContent value="blocks" className="pt-6">
          <VisualBlockEditor eventId={id} event={payload.event} blocks={payload.content_blocks} onChanged={reload} />
        </TabsContent>
        <TabsContent value="pricing" className="pt-6">
          <PaidTiersNoMethodsWarning eventId={id} tiers={payload.pricing_tiers} />
          <PricingPanel eventId={id} event={event} tiers={payload.pricing_tiers} onChanged={reload} />
        </TabsContent>
        <TabsContent value="payments" className="pt-6">
          <PaymentsPanel eventId={id} event={event} />
        </TabsContent>
        <TabsContent value="golden_tickets" className="pt-6">
          <GoldenTicketsPanel eventId={id} />
        </TabsContent>
        <TabsContent value="reg_fields" className="pt-6">
          <RegFieldsPanel eventId={id} />
        </TabsContent>
        <TabsContent value="capacity" className="pt-6">
          <CapacityPanel event={event} onSave={patch} saving={saving} />
        </TabsContent>
        <TabsContent value="seo" className="pt-6">
          <SeoPanel event={event} onSave={patch} saving={saving} />
        </TabsContent>
        <TabsContent value="publish" className="pt-6">
          <PublishPanel event={event} missing={missing} publishReady={publishReady} onPublish={publish} onUnpublish={unpublish} />
        </TabsContent>
        <TabsContent value="survey" className="pt-6">
          <div className="border border-neutral-200 rounded-md p-4 bg-neutral-50" data-testid="survey-tab-content">
            <div className="text-[10px] uppercase tracking-widest text-secondary-800 mb-1">Post-event survey</div>
            <div className="text-sm text-primary-900 font-semibold">Survey console</div>
            <p className="text-xs text-neutral-600 mt-1 max-w-xl">
              Manage the post-event survey campaign for this event: intro copy, participants, send
              actions, responses, testimonials, and connection captures.
            </p>
            {/* Bundle 118 - at-a-glance card so the event manager can
                see how the survey is doing without opening the
                console. Reads the Bundle 118 overview endpoint, which
                is always filtered to the active campaign. */}
            <SurveyAtAGlanceCard eventId={id} />
            <a
              href={`/admin/events/${id}/survey`}
              className="inline-flex items-center gap-1 mt-3 text-sm text-secondary-800 hover:text-secondary-900 underline"
              data-testid="survey-tab-open-console-link"
            >
              Manage survey
            </a>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

/* ---------------------- Reg Fields ---------------------- */

// Bundle 118 - small at-a-glance card the event manager sees on the
// Survey tab of the event edit page. Reads the same overview
// endpoint the console's Overview tab uses so numbers agree.
const SurveyAtAGlanceCard = ({ eventId }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/admin/events/${eventId}/survey/overview`,
        );
        if (!cancelled) setD(data);
      } catch (e) {
        if (!cancelled) {
          setErr(
            e?.response?.data?.detail?.message
              || e?.response?.data?.detail
              || 'unavailable',
          );
        }
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);
  if (err) {
    return (
      <div className="mt-3 text-xs text-neutral-500"
                  data-testid="survey-at-a-glance-empty">
        Survey overview {String(err)}.
      </div>
    );
  }
  if (!d) {
    return (
      <div className="mt-3 text-xs text-neutral-500"
                  data-testid="survey-at-a-glance-loading">
        Loading survey overview...
      </div>
    );
  }
  const c = d.counts || {};
  return (
    <div className="mt-3 rounded-md border border-neutral-200
                          bg-white p-3 flex flex-wrap gap-4 text-xs"
              data-testid="survey-at-a-glance-card">
      <div>
        <div className="text-[10px] uppercase text-neutral-500">
          Invited
        </div>
        <div className="text-lg font-bold text-primary-900
                              tabular-nums"
                    data-testid="survey-glance-invited">
          {c.total_invited || 0}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase text-neutral-500">
          Responded
        </div>
        <div className="text-lg font-bold text-primary-900
                              tabular-nums"
                    data-testid="survey-glance-responded">
          {c.responded || 0}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase text-neutral-500">
          Rate
        </div>
        <div className="text-lg font-bold text-primary-900
                              tabular-nums"
                    data-testid="survey-glance-rate">
          {c.response_rate_pct || 0}%
        </div>
      </div>
      <div className="ml-auto text-[10px] text-neutral-500">
        As of{' '}
        <time dateTime={d.last_computed_at}>
          {new Date(d.last_computed_at).toLocaleString()}
        </time>
      </div>
    </div>
  );
};


/* ---------------------- Reg Fields ---------------------- */
const RegFieldsPanel = ({ eventId }) => {
  const [state, setState] = useState({ all_available: [], required: [], default: [] });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/admin/events/${eventId}/registration-fields`);
      setState(data);
    })();
  }, [eventId]);
  const toggle = (f) => {
    setState(s => ({ ...s, required: s.required.includes(f) ? s.required.filter(x => x !== f) : [...s.required, f] }));
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/events/${eventId}/registration-fields`, { required: state.required });
      setSaved(true); setTimeout(() => setSaved(false), 1500);
    } finally { setSaving(false); }
  };
  return (
    <Card className="border shadow-soft"><CardContent className="p-6 space-y-4">
      <p className="text-sm text-neutral-600">Toggle which fields are required for guest RSVPs. Optional fields still appear in the form and are saved when filled.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {state.all_available.map(f => (
          <label key={f} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${state.required.includes(f) ? 'border-secondary-300 bg-secondary-50/40' : 'border-neutral-200'}`}>
            <input type="checkbox" checked={state.required.includes(f)} onChange={() => toggle(f)} className="h-4 w-4" />
            <span className="text-sm text-primary-900">{f.replace(/_/g, ' ')}</span>
          </label>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving} className="bg-primary-800 hover:bg-primary-900 text-white">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
        </Button>
        {saved && <span className="text-sm text-success-700">Saved</span>}
      </div>
    </CardContent></Card>
  );
};

/* ---------------------- Basics ---------------------- */
const BasicsPanel = ({ event, onSave, saving }) => {
  const _tz = event.timezone || 'America/Chicago';
  const [f, setF] = useState({
    title: event.title || '', slug: event.slug || '', tagline: event.tagline || '',
    summary: event.summary || '', event_type: event.event_type || 'other',
    // Bug X4: store the raw ISO with offset. TzAwareDatetimeInput handles the
    // wall-clock conversion internally so we never touch browser local zone.
    start_datetime: event.start_datetime || null,
    end_datetime: event.end_datetime || null,
    timezone: _tz,
    venue_name: event.venue_name || '', address_line: event.address_line || '',
    city: event.city || '', state: event.state_region || event.state || '',
    country: event.country || '',
    hero_image_url: event.hero_image_url || '',
    hero_image_media_id: event.hero_image_media_id || null,
    hero_image_detail_url: event.hero_image_detail_url || '',
    hero_image_detail_media_id: event.hero_image_detail_media_id || null,
    hero_image_list_url: event.hero_image_list_url || '',
    hero_image_list_media_id: event.hero_image_list_media_id || null,
    enabled_interest_ids: event.enabled_interest_ids || [],
    enabled_activity_ids: event.enabled_activity_ids || [],
  });

  // Load taxonomies for the "Registration Interests & Activities" section
  const [interests, setInterests] = useState([]);
  const [activities, setActivities] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [i, a] = await Promise.all([
          api.get('/admin/taxonomies/interests?include_inactive=false'),
          api.get('/admin/taxonomies/activities?include_inactive=false'),
        ]);
        setInterests(i.data.items || []);
        setActivities(a.data.items || []);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const toggleId = (list, id) => list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const submit = () => onSave({
    basics: {
      title: f.title, slug: f.slug, tagline: f.tagline, summary: f.summary,
      event_type: f.event_type,
      // Bug X4: `f.start_datetime` and `f.end_datetime` are already
      // fully-qualified ISO strings with the correct offset baked in.
      start_datetime: f.start_datetime,
      end_datetime: f.end_datetime,
      timezone: f.timezone,
      venue_name: f.venue_name, address_line: f.address_line,
      city: f.city, state: f.state, country: f.country,
      enabled_interest_ids: f.enabled_interest_ids,
      enabled_activity_ids: f.enabled_activity_ids,
    },
    hero: {
      hero_image_url: f.hero_image_url,
      hero_image_media_id: f.hero_image_media_id,
      hero_image_detail_url: f.hero_image_detail_url,
      hero_image_detail_media_id: f.hero_image_detail_media_id,
      hero_image_list_url: f.hero_image_list_url,
      hero_image_list_media_id: f.hero_image_list_media_id,
    },
  });

  return (
    <Card className="border shadow-soft"><CardContent className="p-6 space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="basics-title" /></Field>
        <Field label="Slug"><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} className="font-mono" /></Field>
      </div>
      <Field label="Tagline"><Input value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} placeholder="One-line hook" /></Field>
      <Field label="Summary"><Textarea value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} rows={4} /></Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Event type">
          <Select value={f.event_type} onValueChange={(v) => setF({ ...f, event_type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Timezone (IANA)">
          <TimezoneSelect
            value={f.timezone}
            onChange={(v) => setF({ ...f, timezone: v })}
          />
        </Field>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Start">
          <TzAwareDatetimeInput
            value={f.start_datetime}
            onChange={(iso) => setF({ ...f, start_datetime: iso })}
            tz={f.timezone}
            data-testid="basics-start-input"
          />
        </Field>
        <Field label="End">
          <TzAwareDatetimeInput
            value={f.end_datetime}
            onChange={(iso) => setF({ ...f, end_datetime: iso })}
            tz={f.timezone}
            data-testid="basics-end-input"
          />
        </Field>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Venue name"><Input value={f.venue_name} onChange={(e) => setF({ ...f, venue_name: e.target.value })} /></Field>
        <Field label="Address"><Input value={f.address_line} onChange={(e) => setF({ ...f, address_line: e.target.value })} /></Field>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Field label="City"><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></Field>
        <Field label="State"><Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} /></Field>
        <Field label="Country"><Input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /></Field>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-secondary-700 mb-1 block">Hero image</Label>
        <HeroImageUploader
          heroImageUrl={f.hero_image_url}
          heroMediaId={f.hero_image_media_id}
          heroImageDetailUrl={f.hero_image_detail_url}
          heroImageDetailMediaId={f.hero_image_detail_media_id}
          heroImageListUrl={f.hero_image_list_url}
          heroImageListMediaId={f.hero_image_list_media_id}
          onChange={(patch) => setF((cur) => ({ ...cur, ...patch }))}
        />
      </div>

      {/* Registration Interests & Activities override */}
      <div className="pt-4 border-t border-neutral-100 space-y-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-secondary-700">Registration interests</Label>
          <p className="text-[11px] text-neutral-500 mt-0.5 mb-2">Leave all unchecked to show every active interest. Select specific ones to restrict the RSVP form to those choices.</p>
          <div className="flex flex-wrap gap-1.5" data-testid="basics-interests-selector">
            {interests.map((it) => {
              const on = f.enabled_interest_ids.includes(it.id);
              return (
                <button key={it.id} type="button" onClick={() => setF({ ...f, enabled_interest_ids: toggleId(f.enabled_interest_ids, it.id) })}
                  className={`px-3 py-1 rounded-full border text-xs transition-colors ${on ? 'border-secondary-500 bg-secondary-100 text-secondary-900' : 'border-neutral-200 bg-white text-neutral-700 hover:border-secondary-300'}`}>
                  {it.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-secondary-700">Registration activities</Label>
          <p className="text-[11px] text-neutral-500 mt-0.5 mb-2">Same rule: none = all active activities.</p>
          <div className="flex flex-wrap gap-1.5" data-testid="basics-activities-selector">
            {activities.map((it) => {
              const on = f.enabled_activity_ids.includes(it.id);
              return (
                <button key={it.id} type="button" onClick={() => setF({ ...f, enabled_activity_ids: toggleId(f.enabled_activity_ids, it.id) })}
                  className={`px-3 py-1 rounded-full border text-xs transition-colors ${on ? 'border-secondary-500 bg-secondary-100 text-secondary-900' : 'border-neutral-200 bg-white text-neutral-700 hover:border-secondary-300'}`}>
                  {it.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <EventQrPanel eventId={event.id} slug={event.slug} />

      <div className="pt-2"><SaveBtn onClick={submit} saving={saving} /></div>
    </CardContent></Card>
  );
};

/* ---------------------- QR Poster panel ---------------------- */
const EventQrPanel = ({ eventId, slug }) => {
  const [copied, setCopied] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const publicUrl = `${window.location.origin}/events/${slug || ''}`;

  useEffect(() => {
    let active = true;
    let objectUrl = null;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}/qr.svg`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok || !active) return;
        const blob = await r.blob();
        objectUrl = URL.createObjectURL(blob);
        if (active) setPreviewUrl(objectUrl);
      } catch (_) { /* ignore */ }
    })();
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [eventId]);

  const download = async (ext) => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}/qr.${ext}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `dostination-${slug || 'event'}-qr.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert('Could not download QR code. Please try again.');
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <div className="pt-4 border-t border-neutral-100" data-testid="qr-panel">
      <Label className="text-xs uppercase tracking-widest text-secondary-700">QR code for posters</Label>
      <p className="text-[11px] text-neutral-500 mt-0.5 mb-3">
        Print this QR on posters and flyers. Guests scan it to reach the event page.
      </p>
      <div className="flex flex-col md:flex-row gap-5 items-start">
        <div className="p-3 rounded-xl border border-neutral-200 bg-white shrink-0 w-[224px] h-[224px] flex items-center justify-center">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Event QR"
              className="w-[200px] h-[200px]"
              data-testid="qr-preview"
            />
          ) : (
            <div className="text-xs text-neutral-400">Loading QR...</div>
          )}
        </div>
        <div className="flex-1 space-y-3">
          <div className="text-xs text-neutral-500 uppercase tracking-widest">Encoded URL</div>
          <code className="block text-xs text-primary-800 bg-neutral-50 border border-neutral-100 rounded-md p-2 break-all">
            {publicUrl}
          </code>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => download('png')} data-testid="qr-download-png-btn">
              Download PNG
            </Button>
            <Button size="sm" variant="outline" onClick={() => download('svg')} data-testid="qr-download-svg-btn">
              Download SVG
            </Button>
            <Button size="sm" variant="ghost" onClick={copyLink} data-testid="qr-copy-link-btn">
              {copied ? 'Link copied' : 'Copy event link'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------------- Content Blocks ---------------------- */
const ContentBlocksPanel = ({ eventId, blocks, onChanged }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newBlockType, setNewBlockType] = useState('overview');

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= blocks.length) return;
    const arr = [...blocks];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    await api.post(`/admin/events/${eventId}/blocks/reorder`, arr.map((b) => b.id));
    onChanged();
  };

  const toggleHidden = async (b) => {
    await api.patch(`/admin/events/${eventId}/blocks/${b.id}`, { hidden: !b.hidden });
    onChanged();
  };

  const del = async (b) => {
    if (!window.confirm('Delete this block?')) return;
    await api.delete(`/admin/events/${eventId}/blocks/${b.id}`);
    onChanged();
  };

  const create = async () => {
    setCreating(true);
    try {
      await api.post(`/admin/events/${eventId}/blocks`, { block_type: newBlockType, hidden: true, content: {} });
      onChanged();
    } finally { setCreating(false); }
  };

  const saveBlock = async (b, content) => {
    await api.patch(`/admin/events/${eventId}/blocks/${b.id}`, { content });
    onChanged();
  };

  return (
    <Card className="border shadow-soft"><CardContent className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Select value={newBlockType} onValueChange={setNewBlockType}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>{BLOCK_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={create} disabled={creating}>
          <Plus className="h-4 w-4 mr-2" /> Add block
        </Button>
      </div>

      <div className="space-y-2">
        {blocks.map((b, i) => (
          <div key={b.id} className={`border rounded-xl ${b.hidden ? 'border-neutral-200 bg-neutral-50/50' : 'border-neutral-200 bg-white'}`}>
            <div className="flex items-center gap-2 p-3">
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="disabled:opacity-30 hover:text-primary-900 text-neutral-500"><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, +1)} disabled={i === blocks.length - 1} className="disabled:opacity-30 hover:text-primary-900 text-neutral-500"><ChevronDown className="h-4 w-4" /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs px-2 py-0.5 bg-neutral-100 rounded">{b.block_type}</span>
                  {b.hidden && <Badge variant="outline" className="text-[10px]">Hidden</Badge>}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => toggleHidden(b)} title={b.hidden ? 'Show' : 'Hide'}>
                {b.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setExpandedId(expandedId === b.id ? null : b.id)}>
                {expandedId === b.id ? 'Close' : 'Edit'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => del(b)} className="text-accent-700 hover:text-accent-900">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {expandedId === b.id && (
              <div className="border-t border-neutral-100 p-4 bg-neutral-50/40">
                <BlockContentEditor block={b} onSave={(content) => saveBlock(b, content)} />
              </div>
            )}
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
};

const BlockContentEditor = ({ block, onSave }) => {
  const [json, setJson] = useState(JSON.stringify(block.content || {}, null, 2));
  const [err, setErr] = useState('');
  const save = () => {
    setErr('');
    try {
      const parsed = JSON.parse(json);
      onSave(parsed);
    } catch (e) {
      setErr('Invalid JSON: ' + e.message);
    }
  };
  return (
    <div className="space-y-2">
      <Label className="text-xs">Content (JSON)</Label>
      <Textarea value={json} onChange={(e) => setJson(e.target.value)} rows={10} className="font-mono text-xs" />
      {err && <div className="text-xs text-accent-700">{err}</div>}
      <div className="flex gap-2">
        <Button size="sm" onClick={save}>Save block</Button>
      </div>
      <p className="text-xs text-neutral-500">Edits the block JSON directly. Each block type has its own shape - see the public event page renderer for expected keys.</p>
    </div>
  );
};

/* ---------------------- Pricing ---------------------- */
const PricingPanel = ({ eventId, event, tiers, onChanged }) => {
  const [form, setForm] = useState({ name: 'Early Bird', price_dollars: '', effective_from: '', effective_until: '' });
  const [selectorDebug, setSelectorDebug] = useState(null);
  const refreshSelector = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/events/${eventId}/tier-selector-debug`);
      setSelectorDebug(data);
    } catch {
      setSelectorDebug(null);
    }
  }, [eventId]);
  React.useEffect(() => { refreshSelector(); }, [refreshSelector, tiers]);
  const create = async () => {
    const cents = Math.round(Number(form.price_dollars || 0) * 100);
    await api.post(`/admin/events/${eventId}/pricing`, {
      name: form.name, price_cents: cents, currency: 'USD',
      // Bug X4: values are already fully-qualified ISO strings from
      // TzAwareDatetimeInput; no browser-local conversion needed.
      effective_from: form.effective_from || null,
      effective_until: form.effective_until || null,
    });
    setForm({ name: 'Regular', price_dollars: '', effective_from: '', effective_until: '' });
    await onChanged();
    await refreshSelector();
  };
  const del = async (id) => {
    if (!window.confirm('Delete this tier?')) return;
    await api.delete(`/admin/events/${eventId}/pricing/${id}`);
    await onChanged();
    await refreshSelector();
  };
  const update = async (id, body) => {
    await api.patch(`/admin/events/${eventId}/pricing/${id}`, body);
    await onChanged();
    await refreshSelector();
  };
  const reorder = async (fromIdx, dir) => {
    const arr = [...(tiers || [])];
    const toIdx = fromIdx + dir;
    if (toIdx < 0 || toIdx >= arr.length) return;
    [arr[fromIdx], arr[toIdx]] = [arr[toIdx], arr[fromIdx]];
    // PATCH each with its new order. Cheaper than a bulk endpoint here.
    await Promise.all(arr.map((t, i) =>
      t.order === i ? null : api.patch(`/admin/events/${eventId}/pricing/${t.id}`, { order: i })
    ));
    onChanged();
  };
  const _tz = event?.timezone || 'America/Chicago';
  return (
    <Card className="border shadow-soft"><CardContent className="p-6 space-y-6">
      <div className="grid gap-3 md:grid-cols-5">
        <Field label="Tier name" className="md:col-span-1"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pricing-new-name" /></Field>
        <Field label="Price ($)"><Input type="number" step="0.01" min="0" placeholder="20.00" value={form.price_dollars} onChange={(e) => setForm({ ...form, price_dollars: e.target.value })} data-testid="pricing-new-price" /></Field>
        <Field label="From">
          <TzAwareDatetimeInput value={form.effective_from} onChange={(iso) => setForm({ ...form, effective_from: iso })} tz={_tz} />
        </Field>
        <Field label="Until">
          <TzAwareDatetimeInput value={form.effective_until} onChange={(iso) => setForm({ ...form, effective_until: iso })} tz={_tz} />
        </Field>
        <div className="flex items-end"><Button className="w-full" onClick={create} data-testid="pricing-new-add-btn"><Plus className="h-4 w-4 mr-2" /> Add tier</Button></div>
      </div>

      <div className="space-y-2">
        {(tiers || []).length === 0 && <p className="text-sm text-neutral-500">No tiers yet. Add at least one to publish.</p>}
        {(tiers || []).map((t, idx) => (
          <PricingTierRow
            key={t.id}
            tier={t}
            tz={_tz}
            index={idx}
            isFirst={idx === 0}
            isLast={idx === (tiers || []).length - 1}
            isSelected={selectorDebug?.selected_tier?.id === t.id}
            onUpdate={(body) => update(t.id, body)}
            onDelete={() => del(t.id)}
            onReorder={(dir) => reorder(idx, dir)}
          />
        ))}
      </div>
      {selectorDebug && (
        <TierSelectorSummary debug={selectorDebug} tz={_tz} />
      )}
    </CardContent>
    <PricingAuditTimeline eventId={eventId} tz={_tz} />
    </Card>
  );
};

/* ---------------------------------------------------------------------- */
/*  Bundle 39 - Pricing audit timeline card                                */
/*  Chronological activate / deactivate / modify entries for this event.   */
/* ---------------------------------------------------------------------- */
const PricingAuditTimeline = ({ eventId, tz }) => {
  const [entries, setEntries] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let ok = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/admin/events/${eventId}/pricing-audit?limit=50`,
        );
        if (ok) setEntries(data.entries || []);
      } catch {
        if (ok) setEntries([]);
      } finally { if (ok) setLoading(false); }
    })();
    return () => { ok = false; };
  }, [eventId]);
  const exportCsv = () => {
    if (!entries || entries.length === 0) return;
    const rows = [[
      'created_at', 'action', 'tier_name', 'tier_price_cents',
      'effective_from_utc', 'effective_until_utc', 'actor_id',
    ]];
    entries.forEach((e) => {
      rows.push([
        e.created_at, e.action, e.tier_name || '', e.tier_price_cents ?? '',
        e.effective_from_utc || '', e.effective_until_utc || '',
        e.actor_id || '',
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `pricing-audit-${eventId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <CardContent className="p-6 pt-0" data-testid="pricing-audit-timeline">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-primary-900">Pricing timeline</h3>
        {entries && entries.length > 0 && (
          <Button
            size="sm" variant="outline" onClick={exportCsv}
            data-testid="pricing-audit-export-csv"
          >
            Export CSV
          </Button>
        )}
      </div>
      {loading && <div className="text-xs text-neutral-500">Loading pricing history...</div>}
      {!loading && (entries?.length ?? 0) === 0 && (
        <div className="text-xs text-neutral-500">
          No pricing history yet. Entries appear here after tiers activate,
          deactivate, or are edited.
        </div>
      )}
      {!loading && (entries?.length ?? 0) > 0 && (
        <ol className="space-y-1" data-testid="pricing-audit-list">
          {entries.map((e) => (
            <PricingAuditRow key={e.id} entry={e} tz={tz} />
          ))}
        </ol>
      )}
    </CardContent>
  );
};

const PricingAuditRow = ({ entry, tz }) => {
  const t = entry.created_at;
  const abs = (() => {
    try { return new Date(t).toLocaleString('en-US', { timeZone: tz, dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return t; }
  })();
  const rel = (() => {
    const ms = Date.now() - Date.parse(t);
    if (ms < 60000) return `${Math.max(1, Math.round(ms / 1000))}s ago`;
    if (ms < 3600000) return `${Math.round(ms / 60000)}m ago`;
    if (ms < 86400000) return `${Math.round(ms / 3600000)}h ago`;
    return `${Math.round(ms / 86400000)}d ago`;
  })();
  const ACTION_STYLE = {
    pricing_tier_activated: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    pricing_tier_deactivated: 'bg-neutral-100 text-neutral-700 border-neutral-200',
    pricing_tier_modified: 'bg-amber-50 text-amber-800 border-amber-200',
  };
  const cls = ACTION_STYLE[entry.action] || 'bg-neutral-50 text-neutral-700 border-neutral-200';
  const priceStr = entry.tier_price_cents != null
    ? `$${(entry.tier_price_cents / 100).toFixed(2)}`
    : '';
  const changeSummary = (() => {
    const c = entry.changes || null;
    if (!c) return null;
    if (c.created) return 'created';
    if (c.deleted) return 'deleted';
    const parts = [];
    if (c.price_cents) parts.push(
      `price $${(c.price_cents.from ?? 0) / 100} -> $${(c.price_cents.to ?? 0) / 100}`,
    );
    if (c.effective_from) parts.push('effective_from changed');
    if (c.effective_until) parts.push('effective_until changed');
    if (c.name) parts.push(`name -> ${c.name.to}`);
    return parts.join(', ');
  })();
  return (
    <li
      className="border border-neutral-200 rounded-lg px-3 py-2 text-xs flex flex-wrap items-center gap-2"
      data-testid={`pricing-audit-row-${entry.id}`}
    >
      <span className="text-neutral-500 font-mono w-16">{rel}</span>
      <span className={`inline-flex items-center px-2 py-0.5 rounded-md border font-semibold ${cls}`}>
        {entry.action.replace('pricing_tier_', '')}
      </span>
      <span className="font-semibold text-primary-900">{entry.tier_name || 'tier'}</span>
      {priceStr && <span className="text-neutral-700">{priceStr}</span>}
      {changeSummary && (
        <span className="text-neutral-500">- {changeSummary}</span>
      )}
      <span className="ml-auto text-[10px] text-neutral-400 font-mono" title={t}>
        {abs}
      </span>
    </li>
  );
};

const PricingTierRow = ({ tier, tz, index, isFirst, isLast, isSelected, onUpdate, onDelete, onReorder }) => {
  // Controlled state so admin can freely edit each field and save on blur/enter.
  const [name, setName] = React.useState(tier.name || '');
  const [priceDollars, setPriceDollars] = React.useState(((tier.price_cents || 0) / 100).toFixed(2));
  // Bug X4: hold the raw ISO. TzAwareDatetimeInput does the tz-local conversion.
  const [effectiveFrom, setEffectiveFrom] = React.useState(tier.effective_from || null);
  const [effectiveUntil, setEffectiveUntil] = React.useState(tier.effective_until || null);
  const [saving, setSaving] = React.useState(false);
  const [savedTick, setSavedTick] = React.useState(false);

  // Reset local state whenever the row's source tier changes (e.g. after reload).
  React.useEffect(() => {
    setName(tier.name || '');
    setPriceDollars(((tier.price_cents || 0) / 100).toFixed(2));
    setEffectiveFrom(tier.effective_from || null);
    setEffectiveUntil(tier.effective_until || null);
  }, [tier.id, tier.name, tier.price_cents, tier.effective_from, tier.effective_until]);

  const commit = async (patch) => {
    setSaving(true);
    try {
      await onUpdate(patch);
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1200);
    } finally { setSaving(false); }
  };

  const onNameBlur = () => {
    if (name !== (tier.name || '')) commit({ name });
  };
  const onPriceBlur = () => {
    const cents = Math.round(Number(priceDollars || 0) * 100);
    if (cents !== (tier.price_cents || 0)) commit({ price_cents: cents });
  };
  const onFromChange = (iso) => {
    setEffectiveFrom(iso);
    commit({ effective_from: iso });
  };
  const onUntilChange = (iso) => {
    setEffectiveUntil(iso);
    commit({ effective_until: iso });
  };

  return (
    <div className="border border-neutral-200 rounded-xl p-4 grid gap-3 md:grid-cols-[auto_1fr_1fr_1fr_1fr_auto] items-end" data-testid={`pricing-row-${tier.id}`}>
      <div className="flex flex-col items-center gap-1 pt-4">
        <Button variant="ghost" size="sm" disabled={isFirst} onClick={() => onReorder(-1)}
                className="h-6 w-6 p-0" data-testid={`pricing-move-up-${tier.id}`}
                aria-label="Move up">
          <ChevronUp className="h-4 w-4" />
        </Button>
        <div className="text-[10px] text-neutral-400 font-mono">#{index + 1}</div>
        <Button variant="ghost" size="sm" disabled={isLast} onClick={() => onReorder(1)}
                className="h-6 w-6 p-0" data-testid={`pricing-move-down-${tier.id}`}
                aria-label="Move down">
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-secondary-700">Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={onNameBlur}
               className="mt-1" data-testid={`pricing-row-name-${tier.id}`} />
        <TierActivityBadge tier={tier} tz={tz} isSelected={isSelected} />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-secondary-700">Price ($)</Label>
        <Input type="number" step="0.01" min="0"
               value={priceDollars}
               onChange={(e) => setPriceDollars(e.target.value)}
               onBlur={onPriceBlur}
               className="mt-1" data-testid={`pricing-row-price-${tier.id}`} />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-secondary-700">Effective from</Label>
        <TzAwareDatetimeInput value={effectiveFrom} onChange={onFromChange} tz={tz}
               className="mt-1" data-testid={`pricing-row-from-${tier.id}`} />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-secondary-700">Effective until</Label>
        <TzAwareDatetimeInput value={effectiveUntil} onChange={onUntilChange} tz={tz}
               className="mt-1" data-testid={`pricing-row-until-${tier.id}`} />
      </div>
      <div className="flex flex-col items-end gap-1 pt-4">
        <Button variant="ghost" size="sm" onClick={onDelete}
                className="text-accent-700 hover:text-accent-900"
                data-testid={`pricing-delete-${tier.id}`}
                aria-label="Delete tier">
          <Trash2 className="h-4 w-4" />
        </Button>
        <div className="text-[10px] h-4 text-secondary-700">
          {saving ? 'Saving...' : savedTick ? 'Saved' : ''}
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------------- */
/*  Bundle 38 - Live tier activity feedback                                */
/*  Shows the admin whether each configured tier is currently ACTIVE, in   */
/*  the future, or expired, and highlights the ONE tier the selector will  */
/*  serve to customers. Kills the "did my change land?" ambiguity.         */
/* ---------------------------------------------------------------------- */
const TierActivityBadge = ({ tier, tz, isSelected }) => {
  const nowMs = Date.now();
  const ef = tier.effective_from ? Date.parse(tier.effective_from) : null;
  const eu = tier.effective_until ? Date.parse(tier.effective_until) : null;
  let label = '';
  let cls = '';
  const active = (ef == null || ef <= nowMs) && (eu == null || eu >= nowMs);
  if (isSelected && active) {
    label = 'ACTIVE - shown to customers';
    cls = 'bg-emerald-100 text-emerald-800 border-emerald-300';
  } else if (active) {
    label = 'Active window (not selected)';
    cls = 'bg-amber-50 text-amber-800 border-amber-200';
  } else if (ef != null && ef > nowMs) {
    const inMs = ef - nowMs;
    const hrs = Math.round(inMs / 3600000);
    const days = Math.round(inMs / 86400000);
    const rel = days >= 2 ? `in ${days}d` : `in ${hrs}h`;
    let local = '';
    try {
      local = new Date(ef).toLocaleString('en-US', { timeZone: tz, dateStyle: 'medium', timeStyle: 'short' });
    } catch { /* ignore invalid tz */ }
    label = `Starts ${rel}${local ? ' - ' + local : ''}`;
    cls = 'bg-neutral-100 text-neutral-700 border-neutral-200';
  } else if (eu != null && eu < nowMs) {
    label = 'Expired';
    cls = 'bg-neutral-200 text-neutral-500 border-neutral-300';
  } else {
    label = 'Inactive';
    cls = 'bg-neutral-100 text-neutral-600 border-neutral-200';
  }
  return (
    <div
      className={`mt-1 inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-semibold ${cls}`}
      data-testid={`pricing-row-activity-${tier.id}`}
    >
      {label}
    </div>
  );
};

const TierSelectorSummary = ({ debug, tz }) => {
  const selected = debug.selected_tier;
  const fallback = debug.fallback_used;
  if (!selected) {
    return (
      <div
        className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        data-testid="pricing-selector-summary"
      >
        No pricing tier configured. Add at least one before publishing.
      </div>
    );
  }
  const priceStr = `$${((selected.price_cents || 0) / 100).toFixed(2)}`;
  const efLocal = (() => {
    try {
      return selected.effective_from
        ? new Date(selected.effective_from).toLocaleString('en-US', { timeZone: tz, dateStyle: 'medium', timeStyle: 'short' })
        : 'no lower bound';
    } catch { return selected.effective_from || ''; }
  })();
  return (
    <div
      className={`mt-2 rounded-md border px-3 py-2 text-xs ${fallback
        ? 'border-amber-300 bg-amber-50 text-amber-900'
        : 'border-emerald-300 bg-emerald-50 text-emerald-900'
      }`}
      data-testid="pricing-selector-summary"
    >
      <strong>{selected.name}</strong> ({priceStr}) is{' '}
      {fallback
        ? 'served as the FALLBACK. No tier is currently within its effective window.'
        : `currently ACTIVE and will show to customers. Effective from ${efLocal}.`}
    </div>
  );
};

// Convert stored ISO string to <input type="datetime-local"> value (local timezone).
// (Legacy helper: only used by the settings-only "created_at" style displays if any.
// New code must use TzAwareDatetimeInput from @/components/ui/TzAwareDatetimeInput.)
function toDatetimeLocal(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}

function fromDatetimeLocal(val) {
  if (!val) return null;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/* ---------------------- Capacity ---------------------- */
const CapacityPanel = ({ event, onSave, saving }) => {
  const _tz = event.timezone || 'America/Chicago';
  const [f, setF] = useState({
    total_capacity: event.total_capacity || '',
    waitlist_enabled: !!event.waitlist_enabled,
    reserve_capacity_minutes: event.reserve_capacity_minutes ?? 15,
    // Bug X4: hold raw ISO; TzAwareDatetimeInput handles the tz-local dance.
    rsvp_deadline: event.rsvp_deadline || null,
    payment_deadline: event.payment_deadline || null,
    show_remaining_spots: !!event.show_remaining_spots,
  });
  return (
    <Card className="border shadow-soft"><CardContent className="p-6 space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Total capacity"><Input type="number" value={f.total_capacity} onChange={(e) => setF({ ...f, total_capacity: e.target.value })} /></Field>
        <Field label="Reserve minutes (hold time)"><Input type="number" value={f.reserve_capacity_minutes} onChange={(e) => setF({ ...f, reserve_capacity_minutes: e.target.value })} /></Field>
        <Field label="RSVP deadline">
          <TzAwareDatetimeInput value={f.rsvp_deadline} onChange={(iso) => setF({ ...f, rsvp_deadline: iso })} tz={_tz} />
        </Field>
        <Field label="Payment deadline">
          <TzAwareDatetimeInput value={f.payment_deadline} onChange={(iso) => setF({ ...f, payment_deadline: iso })} tz={_tz} />
        </Field>
      </div>
      <ToggleRow label="Waitlist enabled" checked={f.waitlist_enabled} onChange={(v) => setF({ ...f, waitlist_enabled: v })} />
      <ToggleRow label="Show exact remaining spots publicly" checked={f.show_remaining_spots} onChange={(v) => setF({ ...f, show_remaining_spots: v })} />
      <SaveBtn onClick={() => onSave({
        capacity: {
          total_capacity: Number(f.total_capacity) || null,
          waitlist_enabled: !!f.waitlist_enabled,
          reserve_capacity_minutes: Number(f.reserve_capacity_minutes) || 15,
          rsvp_deadline: f.rsvp_deadline,
          payment_deadline: f.payment_deadline,
          show_remaining_spots: !!f.show_remaining_spots,
        },
      })} saving={saving} />
    </CardContent></Card>
  );
};

/* ---------------------- SEO ---------------------- */
const SeoPanel = ({ event, onSave, saving }) => {
  const [f, setF] = useState({
    page_title: event.seo_page_title || '',
    meta_description: event.seo_meta_description || '',
    og_image_url: event.seo_og_image_url || '',
    social_share_title: event.seo_social_share_title || '',
    social_share_description: event.seo_social_share_description || '',
    canonical_slug: event.seo_canonical_slug || event.slug || '',
    utm_campaign: event.seo_utm_campaign || '',
    search_indexable: event.seo_search_indexable !== false,
  });
  return (
    <Card className="border shadow-soft"><CardContent className="p-6 space-y-4">
      <Field label="Page title"><Input value={f.page_title} onChange={(e) => setF({ ...f, page_title: e.target.value })} /></Field>
      <Field label="Meta description"><Textarea rows={2} value={f.meta_description} onChange={(e) => setF({ ...f, meta_description: e.target.value })} /></Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Social share title"><Input value={f.social_share_title} onChange={(e) => setF({ ...f, social_share_title: e.target.value })} /></Field>
        <Field label="OG image URL"><Input value={f.og_image_url} onChange={(e) => setF({ ...f, og_image_url: e.target.value })} placeholder="https://…" /></Field>
      </div>
      <Field label="Social share description"><Textarea rows={2} value={f.social_share_description} onChange={(e) => setF({ ...f, social_share_description: e.target.value })} /></Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Canonical slug"><Input value={f.canonical_slug} onChange={(e) => setF({ ...f, canonical_slug: e.target.value })} className="font-mono" /></Field>
        <Field label="UTM campaign"><Input value={f.utm_campaign} onChange={(e) => setF({ ...f, utm_campaign: e.target.value })} className="font-mono" /></Field>
      </div>
      <ToggleRow label="Search engines can index this page" checked={f.search_indexable} onChange={(v) => setF({ ...f, search_indexable: v })} />
      <SaveBtn onClick={() => onSave({ seo: f })} saving={saving} />
    </CardContent></Card>
  );
};

/* ---------------------- Publish ---------------------- */
const PublishPanel = ({ event, missing, publishReady, onPublish, onUnpublish }) => {
  const isPublished = event.state === 'published';
  const wasEverPublished = !!event.published_at;
  const stateLabel = {
    published: 'Published',
    draft: 'Draft',
    scheduled: 'Scheduled',
    archived: 'Archived',
  }[event.state] || event.state;
  const publishedWhen = event.published_at ? new Date(event.published_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  return (
    <Card className="border shadow-soft"><CardContent className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-0.5">Current state</div>
          <div className="font-display text-2xl font-bold text-primary-900" data-testid="publish-current-state">
            {stateLabel}{isPublished && publishedWhen ? ` since ${publishedWhen}` : ''}
          </div>
          {!isPublished && wasEverPublished && publishedWhen && (
            <div className="text-xs text-neutral-500 mt-1">Previously published on {publishedWhen}. You can republish anytime.</div>
          )}
        </div>
        {isPublished && (
          <Button asChild variant="outline"><a href={`/events/${event.slug}`} target="_blank" rel="noopener noreferrer">View public page <ExternalLink className="h-4 w-4 ml-1" /></a></Button>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-xs uppercase tracking-widest text-secondary-700">Required to publish</div>
        {missing.length === 0 ? (
          <div className="flex items-center gap-2 text-success-700"><CheckCircle2 className="h-5 w-5" /> All required fields complete.</div>
        ) : missing.map((m) => (
          <div key={m.field} className="flex items-center gap-2 text-yellow-800">
            <XCircle className="h-4 w-4" /> {m.label}
          </div>
        ))}
      </div>

      <div className="pt-4 border-t border-neutral-100 flex flex-wrap items-center gap-3">
        {isPublished && (
          <>
            <Button
              onClick={onPublish}
              disabled={!publishReady}
              title={publishReady ? '' : 'Complete required fields first'}
              className="bg-primary-800 hover:bg-primary-900 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="publish-save-update-btn"
            >
              <Send className="h-4 w-4 mr-2" /> Save changes & Update
            </Button>
            <Button
              variant="outline"
              onClick={onUnpublish}
              className="border-accent-300 text-accent-800 hover:bg-accent-50"
              data-testid="publish-unpublish-btn"
            >
              Unpublish
            </Button>
          </>
        )}
        {!isPublished && (
          <Button
            onClick={onPublish}
            disabled={!publishReady}
            title={publishReady ? '' : 'Complete required fields first'}
            className="bg-gradient-to-r from-secondary-500 to-secondary-600 text-primary-900 font-semibold shadow-gold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="publish-btn"
          >
            <Send className="h-4 w-4 mr-2" /> {wasEverPublished ? 'Republish' : 'Publish now'}
          </Button>
        )}
      </div>

      <EventPhase10Actions eventId={event.id} slug={event.slug} />
    </CardContent></Card>
  );
};

/* ---------------------- Phase 10 actions: reminders + survey + attendee list ---------------------- */
const EventPhase10Actions = ({ eventId, slug }) => {
  const [busy, setBusy] = useState('');
  const sendReminders = async () => {
    if (!window.confirm('Send check-in reminder emails to all confirmed attendees (skips anyone reminded in the last 24h)?')) return;
    setBusy('reminders');
    try {
      const { data } = await api.post(`/admin/events/${eventId}/check-in-reminders/send`);
      alert(`Reminders sent: ${data.sent}\nSkipped: ${data.skipped}`);
    } catch (e) { alert(e?.response?.data?.detail || 'Send failed'); }
    finally { setBusy(''); }
  };
  const sendSurvey = async () => {
    // Bundle 97 incident fix - the legacy `/survey/send` endpoint
    // emailed real attendees with no test-mode isolation. Route this
    // button to the Survey Console instead so the admin picks
    // explicitly between "Send test" (surrogate inbox, is_test=True)
    // and "Send live" (guarded fan-out with dry-run + idempotency).
    if (!window.confirm(
      'Post-event surveys are now managed on the Survey Console. Open it now?',
    )) return;
    window.location.href = `/admin/events/${eventId}/survey-console`;
  };
  const download = async (fmt) => {
    setBusy('download');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}/attendee-list.${fmt}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('failed');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `attendee-list-${slug || 'event'}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { alert('Download failed'); }
    finally { setBusy(''); }
  };

  return (
    <div className="pt-4 border-t border-neutral-100 space-y-3" data-testid="event-phase10-actions">
      <Label className="text-xs uppercase tracking-widest text-secondary-700">Attendee operations</Label>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={sendReminders} disabled={!!busy} data-testid="send-reminders-btn">
          Send check-in reminders
        </Button>
        <Button variant="outline" size="sm" onClick={sendSurvey} disabled={!!busy} data-testid="send-survey-btn">
          Send survey
        </Button>
        <Button asChild variant="ghost" size="sm">
          <Link to={`/admin/events/${eventId}/survey-results`} data-testid="view-survey-results-btn">View survey results</Link>
        </Button>
        <Button asChild variant="ghost" size="sm">
          <Link to={`/admin/events/${eventId}/connections`} data-testid="view-connections-btn">Suggested introductions</Link>
        </Button>
        <Button asChild variant="ghost" size="sm">
          <Link to={`/admin/events/${eventId}/seating`} data-testid="view-seating-btn">Seating plan</Link>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => download('pdf')} disabled={!!busy} data-testid="download-attendee-pdf-btn">
          Attendee list (PDF)
        </Button>
        <Button variant="ghost" size="sm" onClick={() => download('csv')} disabled={!!busy} data-testid="download-attendee-csv-btn">
          Attendee list (CSV)
        </Button>
      </div>
    </div>
  );
};

/* ---------------------- helpers ---------------------- */
const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <Label className="text-xs uppercase tracking-widest text-secondary-700 mb-1">{label}</Label>
    <div>{children}</div>
  </div>
);

const ToggleRow = ({ label, checked, onChange }) => (
  <div className="flex items-center justify-between p-3 rounded-lg border border-neutral-100 bg-neutral-50/40">
    <span className="text-sm font-medium text-primary-900">{label}</span>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

const SaveBtn = ({ onClick, saving }) => (
  <Button onClick={onClick} disabled={saving} className="bg-primary-800 hover:bg-primary-900 text-white">
    <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
  </Button>
);

// Bug Y1: inline warning when event has paid tiers but zero enabled methods.
const PaidTiersNoMethodsWarning = ({ eventId, tiers }) => {
  const [enabledCount, setEnabledCount] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/events/${eventId}/payment-methods`);
        if (!cancelled) setEnabledCount((data.methods || []).filter((m) => m.enabled).length);
      } catch { if (!cancelled) setEnabledCount(0); }
    })();
    return () => { cancelled = true; };
  }, [eventId]);
  const hasPaid = (tiers || []).some((t) => (t.price_cents || 0) > 0);
  if (!hasPaid || enabledCount === null || enabledCount > 0) return null;
  return (
    <div
      className="mb-4 p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm flex items-start gap-2"
      data-testid="paid-tiers-no-methods-warning"
    >
      <span className="text-lg leading-none">!</span>
      <div>
        <strong>Paid tiers with no payment methods enabled.</strong> Guests
        won&apos;t be able to pay. Go to the <strong>Payments</strong> tab and
        turn on at least one method (Zelle, Venmo, or Stripe).
      </div>
    </div>
  );
};

// Searchable IANA timezone picker. Blocks admins from ever saving free-text
// like "USA (Central Time)" - only valid Intl-supported IDs pass through.
// Uses native <datalist> for the dropdown so we don't need any external
// combobox lib.
const TimezoneSelect = ({ value, onChange }) => {
  const options = React.useMemo(() => {
    try {
      const raw = Intl.supportedValuesOf?.('timeZone') || [];
      // Bubble common Dostination markets to the top for discoverability.
      const preferred = [
        'America/Chicago', 'America/New_York', 'America/Los_Angeles',
        'America/Denver', 'America/Phoenix', 'America/Toronto',
        'Europe/London', 'Asia/Dubai', 'Asia/Kolkata',
      ];
      const preferredSet = new Set(preferred);
      const rest = raw.filter((z) => !preferredSet.has(z));
      return [...preferred.filter((z) => raw.includes(z)), ...rest];
    } catch {
      return ['America/Chicago', 'America/New_York', 'America/Los_Angeles'];
    }
  }, []);
  const isValid = React.useMemo(() => {
    if (!value) return false;
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date());
      return true;
    } catch { return false; }
  }, [value]);
  return (
    <div>
      <Input
        list="admin-tz-options"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="America/Chicago"
        className={!value || isValid ? '' : 'border-accent-500 focus-visible:ring-accent-500'}
        data-testid="admin-event-timezone-input"
      />
      <datalist id="admin-tz-options" data-testid="admin-event-timezone-options">
        {options.map((tz) => <option key={tz} value={tz} />)}
      </datalist>
      {value && !isValid && (
        <div className="text-xs text-accent-700 mt-1" data-testid="admin-event-timezone-invalid">
          Not a valid IANA timezone. Pick one from the dropdown (e.g. America/Chicago).
        </div>
      )}
    </div>
  );
};

export default AdminEventEditPage;
